import { useEffect, useRef, useCallback, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAppDispatch } from '../store/hooks';
import { loadActiveConversations } from '../store/features/activeConversations/activeConversationsSlice';
import { authentication } from '@microsoft/teams-js';

export const useActiveConversationsSignalR = (
    teamId: string | null,
    enabled: boolean = true
) => {
    const dispatch = useAppDispatch();
    const connectionRef = useRef<signalR.HubConnection | null>(null);
    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const retryCountRef = useRef(0);
    const maxRetries = 5;

    const [isConnected, setIsConnected] = useState(false);

    const handleConversationUpdated = useCallback(
        (conversationId: string) => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }

            refreshTimeoutRef.current = setTimeout(() => {
                dispatch(loadActiveConversations());
            }, 500); // Wait 500ms before refreshing
        },
        [dispatch]
    );

    const handleConversationResolved = useCallback(
        (conversationId: string) => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }

            refreshTimeoutRef.current = setTimeout(() => {
                dispatch(loadActiveConversations());
            }, 500);
        },
        [dispatch]
    );

    const handleNewConversation = useCallback(
        (conversationId: string) => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }

            refreshTimeoutRef.current = setTimeout(() => {
                dispatch(loadActiveConversations());
            }, 500);
        },
        [dispatch]
    );

    useEffect(() => {
        if (!enabled || !teamId) return;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(
                `/hubs/activeConversations?teamId=${encodeURIComponent(teamId)}`,
                {
                    accessTokenFactory: async () => {
                        try {
                            const token = await authentication.getAuthToken();
                            return token;
                        } catch (error) {
                            console.error(
                                'Failed to get auth token for SignalR:',
                                error
                            );
                            throw new Error(
                                'Unable to obtain authentication token for SignalR connection'
                            );
                        }
                    },
                }
            )
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    if (retryContext.elapsedMilliseconds < 60000) {
                        return Math.random() * 10000; // 0-10 seconds
                    } else {
                        return 30000; // 30 seconds
                    }
                },
            })
            .configureLogging(signalR.LogLevel.Information)
            .build();

        // Register event handlers
        connection.on('ConversationUpdated', handleConversationUpdated);
        connection.on('ConversationResolved', handleConversationResolved);
        connection.on('NewConversation', handleNewConversation);

        // Connection lifecycle handlers
        connection.onreconnecting((error) => {
            console.log('SignalR reconnecting...', error);
            setIsConnected(false);
        });

        connection.onreconnected((connectionId) => {
            console.log('SignalR reconnected:', connectionId);
            setIsConnected(true);
            retryCountRef.current = 0; // Reset retry count on successful reconnection
            dispatch(loadActiveConversations());
        });

        connection.onclose((error) => {
            console.log('SignalR connection closed:', error);
            setIsConnected(false);
        });

        // Start connection
        const startConnection = async () => {
            try {
                await connection.start();
                console.log('SignalR connected');
                setIsConnected(true);
                retryCountRef.current = 0; // Reset retry count on successful connection
            } catch (err) {
                console.error('SignalR connection error:', err);
                setIsConnected(false);

                // Retry with max limit
                if (retryCountRef.current < maxRetries) {
                    retryCountRef.current++;
                    const delay = Math.pow(2, retryCountRef.current) * 1000; // Exponential backoff
                    console.log(
                        `Retrying connection (attempt ${retryCountRef.current}/${maxRetries}) in ${delay}ms`
                    );

                    retryTimeoutRef.current = setTimeout(
                        startConnection,
                        delay
                    );
                } else {
                    console.error(
                        'Max connection retries exceeded. Please refresh the page.'
                    );
                }
            }
        };

        startConnection();
        connectionRef.current = connection;

        // Cleanup
        return () => {
            // Clear any pending timeouts
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }

            // Stop connection
            if (connectionRef.current) {
                connectionRef.current.stop().catch(console.error);
            }
        };
    }, [
        teamId,
        enabled,
        handleConversationUpdated,
        handleConversationResolved,
        handleNewConversation,
        dispatch,
    ]);

    return {
        connection: connectionRef.current,
        isConnected: isConnected,
    };
};
